import type { StateCreator } from "zustand";

import { createStore } from "./createStore";
import { useUIStore } from "./useUIStore";
import {
  DEFAULT_CHAT_SETTINGS,
  isSilenced,
  repairChannels,
  readChatSettings,
  writeChatSettings,
  type ChatSettings,
  type InputMode,
  type OutputChannel,
} from "../utils/chatSettingsPersistence";
import {
  announceChannelRepair,
  announceTutorAudioDeferred,
  announceWindowCloseDeferred,
  announceWindowCloseRepair,
} from "@/features/tutor/utils/announceChannelRefusal";
import { useAudioStore } from "./useAudioStore";

export type ChannelVerdict =
  | { allowed: true }
  | { allowed: false; attempted: OutputChannel };

const ALLOWED: ChannelVerdict = { allowed: true };
export interface ChatSettingsState {
  inputMode: InputMode;
  tutorAudioOn: boolean;
  chatWindowOpen: boolean;
  captionsAvailable: boolean;
  hydratedSessionId: string | number | null;
  effectiveTutorAudioOn: boolean;
  turnDispatched: boolean;
  pendingWindowClose: boolean;

  hydrate: (sessionId: string | number | null | undefined) => void;
  abandonSession: () => void;
  applyPendingTutorAudio: () => void;
  markTurnDispatched: () => void;
  markTurnSettled: () => void;
  setCaptionsAvailable: (value: boolean) => void;
  setInputMode: (mode: InputMode) => void;
  setTutorAudioOn: (value: boolean) => ChannelVerdict;
  setCaptionsOn: (value: boolean) => ChannelVerdict;
  setChatWindowOpen: (value: boolean, options?: { force?: boolean }) => void;
  getSettings: () => ChatSettings;
  reset: () => void;
}

const INITIAL = {
  inputMode: DEFAULT_CHAT_SETTINGS.inputMode,
  tutorAudioOn: DEFAULT_CHAT_SETTINGS.tutorAudioOn,
  chatWindowOpen: false,
  captionsAvailable: true,
  hydratedSessionId: null as string | number | null,
  effectiveTutorAudioOn: DEFAULT_CHAT_SETTINGS.tutorAudioOn,
  turnDispatched: false,
  pendingWindowClose: false,
};

const applyCaptions = (value: boolean): void => {
  const ui = useUIStore.getState();
  if (ui.captionsOpen !== value) ui.setCaptionsOpen(value);
};

const base: StateCreator<ChatSettingsState> = (set, get) => {
  // isAITalking only turns true at START. Between sending TURN_CHANGE and that
  // START the turn is already committed on the wire under the old value, so a
  // toggle in that window has to defer too — otherwise the frontend and the
  // backend disagree about whether the turn has audio, and a turn the backend
  // is synthesising silently never gets a clock.
  const isTutorResponding = (): boolean =>
    useAudioStore.getState().isAITalking || get().turnDispatched;

  const commit = (
    next: ChatSettings,
    {
      chatWindowOpen = get().chatWindowOpen,
      target,
    }: { chatWindowOpen?: boolean; target?: OutputChannel } = {},
  ): ChatSettings => {
    const repaired = repairChannels(next, {
      chatWindowOpen,
      captionsAvailable: get().captionsAvailable,
      target,
    });

    set({
      inputMode: repaired.inputMode,
      tutorAudioOn: repaired.tutorAudioOn,
      chatWindowOpen,
      ...(isTutorResponding()
        ? {}
        : { effectiveTutorAudioOn: repaired.tutorAudioOn }),
    });
    applyCaptions(repaired.captionsOn);
    writeChatSettings(get().hydratedSessionId, repaired);

    return repaired;
  };

  const request = (
    next: ChatSettings,
    attempted: OutputChannel,
  ): ChannelVerdict => {
    if (
      isSilenced(next, {
        chatWindowOpen: get().chatWindowOpen,
        captionsAvailable: get().captionsAvailable,
      })
    ) {
      return { allowed: false, attempted };
    }

    commit(next);
    return ALLOWED;
  };

  const closingSilencesTurn = (settings: ChatSettings): boolean =>
    isTutorResponding() &&
    !get().effectiveTutorAudioOn &&
    !(settings.captionsOn && get().captionsAvailable);

  const applyWindowClose = (): void => {
    const before = get().getSettings();
    const after = commit(before, { chatWindowOpen: false, target: "captions" });
    set({ pendingWindowClose: false });
    if (!before.tutorAudioOn && after.tutorAudioOn) {
      if (get().effectiveTutorAudioOn) {
        announceWindowCloseRepair("audio");
      } else {
        announceTutorAudioDeferred();
      }
    } else if (!before.captionsOn && after.captionsOn) {
      announceWindowCloseRepair("captions");
    }
  };

  return {
    ...INITIAL,

    hydrate: (sessionId) => {
      const id = sessionId ?? null;

      // Hydrating the session already loaded must do nothing. Boot hydrates
      // imperatively so it can read the settings before starting the opener,
      // and the layout effect then hydrates the same id again on the render
      // the URL update schedules — clearing the flags there would settle an
      // opener that is still waiting for START. Leaving a session is what
      // clears them; see abandonSession.
      if (id !== null && get().hydratedSessionId === id) return;

      // Entering a lesson has nothing in flight by definition, so the
      // hydrated value is forced through rather than deferred.
      set({
        hydratedSessionId: id,
        turnDispatched: false,
        pendingWindowClose: false,
      });
      useAudioStore.getState().setIsAITalking(false);
      const hydrated = commit(readChatSettings(sessionId), {
        chatWindowOpen: false,
      });
      set({ effectiveTutorAudioOn: hydrated.tutorAudioOn });
    },

    // Unmounting mid-response leaves nothing to settle the turn: the
    // websocket cleanup suppresses WS_DISCONNECTED and the PCM cleanup does
    // not touch isAITalking. The session id is deterministic, so without this
    // the next visit to the same lesson would start holding a turn that ended
    // when the student walked away.
    abandonSession: () => {
      useAudioStore.getState().setIsAITalking(false);
      set({
        turnDispatched: false,
        pendingWindowClose: false,
        effectiveTutorAudioOn: get().tutorAudioOn,
      });
    },

    setCaptionsAvailable: (value) => {
      if (get().captionsAvailable === value) return;
      const wasMuted = !get().tutorAudioOn;
      set({ captionsAvailable: value });
      commit(get().getSettings());
      if (wasMuted && get().tutorAudioOn) {
        // Losing captions while muted is the one case that must not wait for
        // the next turn: deferring it leaves audio off and captions gone, so
        // the student can neither hear nor read — the exact dead end the
        // guardrail exists to prevent.
        //
        // But a turn already admitted as muted cannot retroactively gain
        // audio — the backend selected MockTTSService for it before this
        // fired. Forcing effectiveTutorAudioOn true here anyway is worse
        // than the dead end: on START the frontend would see audio enabled
        // and never arm the silent clock, and since the mock turn produces
        // no AUDIO either, nothing would ever end it. So the repair only
        // takes effect immediately when no turn currently owns the old
        // value; a turn already in flight picks it up at its own next
        // boundary, same as any other deferred toggle — and gets that
        // toggle's own copy, since CHANNEL_REPAIR_COPY reads as
        // already-in-effect ("has been turned back on").
        if (isTutorResponding() && !get().effectiveTutorAudioOn) {
          announceTutorAudioDeferred();
        } else {
          set({ effectiveTutorAudioOn: true });
          announceChannelRepair();
        }
      }
    },

    setInputMode: (mode) =>
      void commit(
        { ...get().getSettings(), inputMode: mode },
        {
          chatWindowOpen: mode === "chat" && get().chatWindowOpen,
          target: mode === "voice" ? "audio" : undefined,
        },
      ),

    setTutorAudioOn: (value) => {
      const verdict = request(
        { ...get().getSettings(), tutorAudioOn: value },
        "audio",
      );
      if (
        verdict.allowed &&
        get().tutorAudioOn &&
        !get().effectiveTutorAudioOn
      ) {
        announceTutorAudioDeferred();
      }
      return verdict;
    },

    setCaptionsOn: (value) =>
      request({ ...get().getSettings(), captionsOn: value }, "captions"),

    setChatWindowOpen: (value, { force = false } = {}) => {
      if (value) {
        set({ pendingWindowClose: false });
        void commit(get().getSettings(), {
          chatWindowOpen: true,
          target: "captions",
        });
        return;
      }
      if (!force && closingSilencesTurn(get().getSettings())) {
        set({ pendingWindowClose: true });
        announceWindowCloseDeferred();
        return;
      }
      applyWindowClose();
    },

    getSettings: () => ({
      inputMode: get().inputMode,
      tutorAudioOn: get().tutorAudioOn,
      captionsOn: useUIStore.getState().captionsOpen,
    }),

    markTurnDispatched: () => set({ turnDispatched: true }),

    markTurnSettled: () => {
      set({ turnDispatched: false });
      if (get().pendingWindowClose) applyWindowClose();
    },

    applyPendingTutorAudio: () => {
      const { tutorAudioOn, effectiveTutorAudioOn } = get();
      if (tutorAudioOn === effectiveTutorAudioOn) return;
      set({ effectiveTutorAudioOn: tutorAudioOn });
    },

    reset: () => set(INITIAL),
  };
};

export const useChatSettingsStore = createStore<ChatSettingsState>(
  "ChatSettingsStore",
  base,
);
